import React, {Fragment, useState} from "react";
import {Dialog, DialogPanel, DialogTitle, Transition, TransitionChild} from "@headlessui/react";
import {Button, Flex, Link, Text} from "@chakra-ui/react";
import {BsLightningChargeFill} from "react-icons/bs";
import {useCreditsStore} from "../../store/useCreditsStore.ts";
import SmartIcon from "../SmartIcon.tsx";
import {CREDITS_CONTACT_EMAIL} from "./PricingPackageCard.tsx";

// Headless UI Dialog, not a Chakra Modal: it must stay interactive above the Create dialog.

const NotEnoughCreditsModal: React.FC = () => {
  const notEnough = useCreditsStore((state) => state.notEnough);
  const closeNotEnough = useCreditsStore((state) => state.closeNotEnough);
  const openPricing = useCreditsStore((state) => state.openPricing);
  // The flag clears before the leave transition ends; the last payload keeps the caption filled.
  const [payload, setPayload] = useState(notEnough);
  if (notEnough !== null && notEnough !== payload) setPayload(notEnough);

  const handleUpgrade = (): void => {
    closeNotEnough();
    openPricing();
  };

  return (
    <Transition show={notEnough !== null} as={Fragment}>
      <Dialog className="fixed inset-0 z-[60] flex p-6 overflow-auto md:px-4" onClose={closeNotEnough}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-n-700/75" aria-hidden="true"/>
        </TransitionChild>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative z-10 m-auto w-full max-w-[28rem] rounded-3xl bg-white p-6 text-n-700">
            <DialogTitle as="h2" className="h6 pr-12 text-n-700">Not enough credits</DialogTitle>
            <Text mt={3} fontSize="sm" color="n.500" data-testid="not-enough-body">
              You don't have enough credits to continue translating movies. Click the Upgrade button to upgrade your package, or contact us at <Link href={`mailto:${CREDITS_CONTACT_EMAIL}`} color="brandPurple.500" fontWeight="semibold" _hover={{color: "brandPurple.600"}}>{CREDITS_CONTACT_EMAIL}</Link>.
            </Text>
            {payload && (
              <Text mt={2} fontSize="xs" color="n.400" data-testid="not-enough-caption">
                {`This job needs ${payload.requiredCredits} credits; you have ${payload.availableCredits}.`}
              </Text>
            )}
            <Flex justify="center" mt={6}>
              <Button
                leftIcon={<BsLightningChargeFill/>}
                bg="brandPurple.500"
                color="white"
                _hover={{bg: "violetPurple.500"}}
                _active={{bg: "brandPurple.600"}}
                rounded="lg"
                px={8}
                onClick={handleUpgrade}
              >
                Upgrade
              </Button>
            </Flex>
            <button
              type="button"
              aria-label="Close dialog"
              className="absolute top-5 right-5 flex h-9 w-9 items-center justify-center rounded-full bg-n-200 fill-n-700 text-0 transition-colors hover:bg-n-300"
              onClick={closeNotEnough}
            >
              <SmartIcon className="fill-inherit transition-colors" name="close" size={20}/>
            </button>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

export default NotEnoughCreditsModal;
